import { useState } from 'react';
import { euro } from '@fleet/shared';

/**
 * Stacked bar chart, hand-rolled with divs. No chart library, so no extra bundle.
 *
 * The bars share the full width of their panel instead of being fixed-width and
 * left-aligned: twelve months in a wide panel would otherwise leave half of it
 * empty. Each bar is capped so five yearly bars do not become slabs.
 *
 * **Every amount lives in one place**: the read-out line under the bars. Not in
 * labels above them (twelve of those collide on a phone), not in a floating
 * tooltip (a phone has no hover), and no longer on the legend buttons either —
 * those carried the whole-series total per layer one line below the same layer's
 * amount for one month, in the same colour under the same word, with nothing to
 * say which was which.
 *
 * So the read-out has two states and its label always names the one you are in:
 * point at nothing and it totals the series, point at a bar and it shows that
 * period. Hover or focus scrubs it on a desktop, a tap parks it on a phone, and
 * each bar's hit area is the full column height so a 24px bar is still easy to
 * hit. The legend is what it always really was: a filter.
 */
export interface BarLaag {
  key: string;
  label: string;
  color: string;
}

export interface Staaf {
  key: string;
  label: string;
  /** Amount per layer key; missing or 0 draws nothing. */
  waarden: Record<string, number>;
  /** Period lies ahead: nothing happened there yet, so the label steps back. */
  gedempt?: boolean;
}

export function StackedBars({
  lagen,
  staven,
  hoogte = 190,
  eenheid = 'euro',
  legenda = true,
  maxBreedte = 64,
  totaalLabel = 'Totaal',
}: {
  lagen: readonly BarLaag[];
  staven: readonly Staaf[];
  /** Pixel height of the tallest bar; labels sit outside it. */
  hoogte?: number;
  eenheid?: 'euro' | 'km';
  legenda?: boolean;
  /** Cap on how thick one bar gets when there are only a few. */
  maxBreedte?: number;
  /** What the resting state is called, e.g. "2026" or "Alle jaren". */
  totaalLabel?: string;
}) {
  const [uit, setUit] = useState<Record<string, boolean>>({});
  const [actief, setActief] = useState<string | null>(null);
  const aan = (k: string) => !uit[k];
  const fmt = (n: number) =>
    eenheid === 'euro' ? euro(n) : `${Math.round(n).toLocaleString('nl-NL')} km`;

  const totaalPerStaaf = staven.map((s) =>
    lagen.reduce((sum, l) => sum + (aan(l.key) ? (s.waarden[l.key] ?? 0) : 0), 0),
  );
  const max = Math.max(1, ...totaalPerStaaf);
  const perLaag = Object.fromEntries(
    lagen.map((l) => [l.key, staven.reduce((sum, s) => sum + (s.waarden[l.key] ?? 0), 0)]),
  );

  // De leesregel heeft twee standen. Wijs je niets aan, dan staat er het totaal
  // over de hele reeks; wijs je een staaf aan, dan die periode. Eén plek met
  // bedragen dus, met het label ervoor dat zegt waar je naar kijkt.
  //
  // Die bedragen stonden eerder óók op de legendaknoppen, één regel lager, met
  // dezelfde kleur en hetzelfde woord ervoor maar een andere periode: naast
  // "Afschrijving EUR 8.486,47" van augustus stond "Afschrijving EUR 62.197,90"
  // van het jaar. De knoppen zijn nu wat ze al waren, een filter.
  const gekozenIndex = actief ? staven.findIndex((s) => s.key === actief) : -1;
  const zicht = gekozenIndex >= 0 ? staven[gekozenIndex] : null;
  const zichtWaarden: Record<string, number> = zicht
    ? zicht.waarden
    : Object.fromEntries(lagen.map((l) => [l.key, perLaag[l.key] ?? 0]));
  const zichtTotaal = zicht
    ? (totaalPerStaaf[gekozenIndex] ?? 0)
    : lagen.reduce((sum, l) => sum + (aan(l.key) ? (perLaag[l.key] ?? 0) : 0), 0);
  const zichtLabel = zicht ? zicht.label : totaalLabel;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, padding: '4px 0' }}>
        {staven.map((s, i) => {
          const totaal = totaalPerStaaf[i] ?? 0;
          const segmenten = lagen.filter((l) => aan(l.key) && (s.waarden[l.key] ?? 0) > 0);
          const gekozen = zicht?.key === s.key;
          return (
            <button
              key={s.key}
              type="button"
              aria-label={`${s.label}: ${fmt(totaal)}`}
              aria-pressed={gekozen}
              onPointerEnter={() => setActief(s.key)}
              // Op een telefoon blijft de keuze staan tot je een andere staaf tikt;
              // alleen een muis laat hem weer los.
              onPointerLeave={(e) => {
                if (e.pointerType === 'mouse') setActief(null);
              }}
              onFocus={() => setActief(s.key)}
              onClick={() => setActief(s.key)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 7,
                flex: '1 1 0',
                minWidth: 0,
                padding: '6px 0 0',
                border: 'none',
                borderRadius: 3,
                background: gekozen ? '#F4EEE4' : 'transparent',
                font: 'inherit',
                cursor: 'pointer',
                transition: 'background .15s ease',
                animation: 'vsRise .5s cubic-bezier(.4,0,.2,1) both',
                animationDelay: `${i * 0.035}s`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column-reverse',
                  justifyContent: 'flex-start',
                  width: '100%',
                  maxWidth: maxBreedte,
                  height: hoogte,
                }}
              >
                {segmenten.length ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column-reverse',
                      width: '100%',
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: '0 1px 3px rgba(17,17,17,0.12)',
                    }}
                  >
                    {segmenten.map((l) => (
                      <div
                        key={l.key}
                        title={`${l.label}: ${fmt(s.waarden[l.key] ?? 0)}`}
                        style={{
                          width: '100%',
                          height: Math.max(2, Math.round(((s.waarden[l.key] ?? 0) / max) * hoogte)),
                          background: l.color,
                          transformOrigin: 'bottom',
                          animation: 'vsGrowUp .6s cubic-bezier(.4,0,.2,1) both',
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  // Een maand zonder kosten blijft in de reeks staan, als nullijn.
                  <div style={{ width: '100%', height: 2, background: '#E3D9CC' }} />
                )}
              </div>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: gekozen ? 900 : 700,
                  color: gekozen ? '#111111' : s.gedempt ? '#BDB3A5' : '#666666',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                  padding: '0 1px 4px',
                }}
              >
                {s.label}
              </span>
            </button>
          );
        })}
      </div>

      <div
        style={{
          marginTop: 12,
          paddingTop: 12,
          borderTop: '1px solid #E3D9CC',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          gap: '6px 14px',
          minHeight: 46,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 900, color: '#111111' }}>{zichtLabel}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#2A6085' }}>{fmt(zichtTotaal)}</span>
        {zichtTotaal === 0 ? (
          <span style={{ fontSize: 12, color: '#999999' }}>geen kosten geboekt</span>
        ) : (
          lagen.length > 1 &&
          lagen
            .filter((l) => aan(l.key) && (zichtWaarden[l.key] ?? 0) > 0)
            .map((l) => (
              <span
                key={l.key}
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}
              >
                <span
                  style={{
                    width: 9,
                    height: 9,
                    flex: 'none',
                    borderRadius: 2,
                    background: l.color,
                  }}
                />
                <span style={{ color: '#666666' }}>{l.label}</span>
                <span style={{ fontWeight: 700, color: '#111111' }}>
                  {fmt(zichtWaarden[l.key] ?? 0)}
                </span>
              </span>
            ))
        )}
      </div>

      {legenda && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
          {lagen.map((l) => {
            const on = aan(l.key);
            return (
              <button
                key={l.key}
                type="button"
                onClick={() => setUit((u) => ({ ...u, [l.key]: !u[l.key] }))}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 12px',
                  border: `1px solid ${on ? '#111111' : '#C9BBA8'}`,
                  borderRadius: 2,
                  background: on ? '#FFFFFF' : '#F4EEE4',
                  cursor: 'pointer',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: 12,
                  fontWeight: 700,
                  color: on ? '#111111' : '#999999',
                  opacity: on ? 1 : 0.7,
                }}
              >
                <span
                  style={{
                    width: 11,
                    height: 11,
                    flex: 'none',
                    borderRadius: 2,
                    background: on ? l.color : 'transparent',
                    border: `2px solid ${l.color}`,
                  }}
                />
                <span>{l.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
